
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ProfileData } from "@/types/profile";

export const useProfileFetch = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileData>({
    company_name: "",
    notification_email: "",
    email_notifications_enabled: true,
    preferred_industries: [],
    preferred_countries: [],
    price_filter_enabled: false,
    price_filter_currency: "EUR",
    notification_frequency: "daily" // Default notification frequency
  });

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/login");
          return;
        }
        
        // Get the current user's profile using their ID
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();

        if (error) {
          console.error("Error fetching profile:", error);
          toast({
            title: "Error",
            description: "Failed to load profile data",
            variant: "destructive",
          });
          return;
        }

        if (data) {
          console.log("Loaded profile data:", data);
          setProfile({
            company_name: data.company_name || "",
            notification_email: data.notification_email || "",
            email_notifications_enabled: data.email_notifications_enabled ?? true,
            preferred_industries: data.preferred_industries || [],
            preferred_countries: data.preferred_countries || [],
            min_price_range: data.min_price_range,
            max_price_range: data.max_price_range,
            search_keywords: data.search_keywords || "",
            price_filter_enabled: data.price_filter_enabled ?? false,
            price_filter_currency: data.price_filter_currency || "EUR",
            notification_frequency: data.notification_frequency || "daily",
            email: data.email,
            id: data.id,
            created_at: data.created_at,
            updated_at: data.updated_at,
            last_notification_sent: data.last_notification_sent,
          });
        }
      } catch (error) {
        console.error("Error in checkUser:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, [navigate, toast]);

  return {
    profile,
    setProfile,
    loading,
  };
};
